import React from 'react';

import { Stack, Text, Group, NumberInput, Select, Button } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import useCreateExpectations from 'hooks/reports/utilization/package-utilization/useBulkCreateExpectations';
import notificationHandler from 'utils/notifications';

import { useModal } from '../../Context/ModalContext';
import AttendanceHelpButton from '../Components/AttendanceHelpButton';
import PackageForExpectationsMultiSelect from '../Components/PackageForExpectationsMultiselect';

interface FormValues {
  packages: string[];
  frequencyValue: number;
  frequencyUnit: 'week' | 'month' | 'year';
}

const Form = ({ className }: { className?: string }): JSX.Element => {
  const { openModal } = useModal();
  const { mutate: createExpectations, isLoading } = useCreateExpectations();
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    validate: {
      packages: isNotEmpty('Please select at least one package'),
      frequencyValue: isNotEmpty('Please enter the number of times'),
      frequencyUnit: isNotEmpty('Please select a time period'),
    },
  });

  const handleSubmit = (values: FormValues): void => {
    createExpectations(
      {
        packages: values.packages,
        frequencyValue: values.frequencyValue.toString(),
        frequencyUnit: values.frequencyUnit,
      },
      {
        onSuccess: () => {
          openModal('list');
          successNotification('The package has been added to the report successfully.');
        },
        onError: error => {
          errorNotification('The package could not be added to the report.', error.response?.data.message);
        },
      },
    );
  };

  return (
    <form className={className} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing='md'>
        <PackageForExpectationsMultiSelect
          label='Select packages'
          placeholder='Select packages'
          hasExpectation={false}
          limit={20}
          required
          {...form.getInputProps('packages')}
        />

        <Group align='center' spacing='xs'>
          <Text size='md'>Define attendance expectation</Text>
          <AttendanceHelpButton position='bottom' withArrow withinPortal width={300} />
        </Group>

        <Group align='start' position='apart'>
          <Group align='start'>
            <NumberInput
              placeholder='Enter times'
              required
              min={0.01}
              step={1}
              precision={2}
              {...form.getInputProps('frequencyValue')}
            />
            <Text size='sm' pt='0.5rem'>
              times per
            </Text>
            <Select
              placeholder='Select time'
              data={['week', 'month', 'year']}
              required
              clearable
              {...form.getInputProps('frequencyUnit')}
            />
          </Group>
          <Group spacing='xs'>
            <Button variant='filled' color='red' onClick={() => openModal('list')}>
              Cancel
            </Button>
            <Button variant='filled' type='submit' disabled={!form.isDirty()} loading={isLoading}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
};

export default Form;
